import type { ReactNode } from "react";

import type { ChipClass } from "../shift-readiness.ts";

/**
 * The frame every screen in both design files is drawn in, built once.
 *
 * `11 Driver App.dc.html` and `15 WP14 Driver Onboarding and Earnings.dc.html` are the same page
 * body with different content: byte for byte, the status bar, the banner, the hero with its app bar,
 * and the sheet with its handle, head, check rows, key/value rows, thumbnails, note and action group
 * are identical in the two files, and only the `SC` table differs. So this is one component rather
 * than eight, and the eight screens are eight sets of values passed into it — which is also what
 * makes a difference between two screens a difference in the data rather than in the markup.
 *
 * The DOM below is the design's own, element for element and in its order:
 *
 *     screen
 *       [banner]                role="status", amber, 800 weight
 *       top                     the hero: the illustration and the app bar over it
 *         [art]                 the illustration, where the screen has one
 *         appBar                the online pill and the earnings pill
 *       sheet
 *         handle
 *         head                  chip, then title and sub
 *         check*                zero or more precondition rows
 *         [rows]                the key/value box
 *         [thumbs]              three squares
 *         [note]                info or pending
 *         actions               the 56dp buttons, pinned to the floor
 *
 * ## The one row that is not here, and why
 *
 * The design's first row inside `screen` is the device's status bar: a clock, a signal glyph, a
 * battery glyph and the green dot its notes call "background location shows as a green dot in the
 * status bar whenever the driver is online". A web page cannot draw that row from anything it knows.
 * The signal strength and the battery level are not published to a document on the browsers this
 * product supports, and drawing them from nothing is the one thing these rules forbid without
 * qualification. The clock and the dot could be drawn and would then sit alone in a band whose other
 * half was invented, which is worse than a band this application does not claim.
 *
 * So the application starts at the banner. The acceptance check hides that row on the design side
 * and shortens the design's own `screen` by exactly its measured height, so the comparison is of the
 * same geometry rather than nearly it. The green location dot is the one designed element genuinely
 * lost. Recorded as R-29 and Q-58.
 */

/** The amber strip: `6 Offline queue`'s `cloud_off` and `7 Document expiring`'s `schedule`. */
export interface Banner {
  readonly glyph: string;
  readonly text: string;
}

/** One check row, already decided by a logic module. */
export interface CheckProps {
  readonly key: string;
  readonly status: "ok" | "bad" | "warn" | "wait";
  readonly glyph: string;
  readonly title: string;
  readonly sub: string;
  readonly subIsAbsence?: boolean;
}

/** One key/value row. `value: null` is an absence and is drawn as one. */
export interface RowProps {
  readonly key: string;
  readonly value: string | null;
  readonly because?: string;
}

/** The sheet's note: `info` is a blue card, `pending` is a dashed outline. */
export interface NoteProps {
  readonly kind: "info" | "pending";
  readonly text: string;
}

/** One 56dp action. `slide` is the design's slide-to-confirm for anything that cannot be undone. */
export interface ActionProps {
  readonly label: string;
  readonly glyph: string;
  readonly primary: boolean;
  readonly slide?: boolean;
  readonly onPress?: () => void;
  /**
   * Why this action cannot be carried out, when the platform publishes no route for it.
   *
   * The button is still drawn, because the design draws it and nothing may be removed. Pressing it
   * says this instead of appearing to work. `7 Document expiring`'s two buttons are both in this
   * state: `Upload renewed permit` can now reach FIL-02 and still cannot attach what it uploads,
   * because DRV-03's document write needs a scope a courier does not hold, and `Remind me later`
   * needs a deferral nothing publishes.
   */
  readonly unavailable?: string;
}

export interface PhoneShellProps {
  /** The screen family's own class, which anchors its stylesheet: `readiness`, `offers`, … */
  readonly screen: string;
  /**
   * True when this screen's hero is the design's map hero rather than an illustration.
   *
   * It changes two things and draws nothing: the hero's height (the design's `topH` is 210 for a
   * map screen with two rows or fewer, against 160 or 220 otherwise) and its background (`C.map`
   * rather than the radial wash). **The map itself is not drawn, because the design does not draw
   * one** — see the note below the component and Q-68.
   */
  readonly map?: boolean;
  readonly banner?: Banner;
  /** The illustration. Omitted when `map` is true, as the design omits it. */
  readonly art?: ReactNode;
  /** The app bar's online pill. The design's `on` flag, per screen. */
  readonly online: boolean;
  /**
   * The app bar's earnings pill, which has no source on any screen in either file.
   *
   * The design prints `TZS 7,200` on all eighteen. Nothing publishes what a courier has earned —
   * R-22 — so this is `null` and the pill carries the design's own `unknown` mark instead of a
   * figure. The pill is narrower than the design's as a result, which is a real and unavoidable
   * difference and is reported as one rather than closed by inventing a number.
   */
  readonly earningsLabel: string | null;
  readonly chip: { readonly class: ChipClass; readonly label: string };
  readonly title: string;
  /**
   * Why the end of the title is missing, when part of the design's headline has no source.
   *
   * `8 Proof` is the screen this exists for. Its headline is `Delivered to Amina K.` and nothing
   * publishes who took the parcel, so the title is `Delivered to` and this carries the design's own
   * `unknown` mark after it. The sentence stops where the platform's knowledge stops, which is a
   * different statement from a sentence completed with a dash or with the word "unknown" — and very
   * different from one completed with a name.
   */
  readonly titleAbsence?: string;
  readonly sub: string;
  readonly checks?: readonly CheckProps[];
  readonly rows?: readonly RowProps[];
  readonly thumbs?: readonly { readonly glyph: string; readonly label: string }[];
  readonly note?: NoteProps;
  readonly actions?: readonly ActionProps[];
  /** Said under the title when the words on this screen are this build's rather than the design's. */
  readonly flag?: string;
}

export function PhoneShell(props: PhoneShellProps): ReactNode {
  const classes = [
    "screen",
    props.screen,
    props.banner === undefined ? undefined : "screen--banner",
    props.map === true ? "screen--map" : undefined,
  ]
    .filter((name) => name !== undefined)
    .join(" ");

  return (
    <div className={classes}>
      {props.banner === undefined ? null : (
        <div className="screen__banner" role="status">
          <span className="glyph" aria-hidden="true">
            {props.banner.glyph}
          </span>
          <span className="screen__banner-text">{props.banner.text}</span>
        </div>
      )}

      <div className="screen__top">
        {props.art === undefined ? null : <div className="screen__art">{props.art}</div>}
        <div className="screen__appbar">
          <span className="pill">
            <span className={`pill__dot${props.online ? " pill__dot--online" : ""}`} />
            {props.online ? "Online" : "Offline"}
          </span>
          <span className="pill pill--earnings">
            {props.earningsLabel === null ? (
              /*
               * The design's `unknown` glyph at the pill's own scale.
               *
               * `title` rather than visible words, because the pill is 36dp tall and the design
               * gives it one line: a courier who wants to know why opens the earnings screen, which
               * says it in full.
               */
              <span className="rows__absent" title="Nothing publishes what you have earned" />
            ) : (
              props.earningsLabel
            )}
          </span>
        </div>
      </div>

      <div className="sheet">
        <span className="sheet__handle" />

        <div className="sheet__head">
          <StateChip chipClass={props.chip.class} label={props.chip.label} />
          <div className="sheet__headings">
            <strong className="sheet__title">
              {props.title}
              {props.titleAbsence === undefined ? null : (
                <>
                  {" "}
                  <span className="rows__absent" title={props.titleAbsence} />
                </>
              )}
            </strong>
            <span className="sheet__sub">{props.sub}</span>
            {props.flag === undefined ? null : <span className="sheet__flag">{props.flag}</span>}
          </div>
        </div>

        {(props.checks ?? []).map((check) => (
          <div key={check.key} className={`check check--${check.status}`}>
            <span className="glyph check__glyph" aria-hidden="true">
              {check.glyph}
            </span>
            <span className="check__text">
              <span className="check__title">{check.title}</span>
              <span className="check__sub">{check.sub}</span>
            </span>
          </div>
        ))}

        {props.rows === undefined || props.rows.length === 0 ? null : (
          <div className="rows">
            {props.rows.map((row) => (
              <div key={row.key} className="rows__row">
                <span className="rows__key">{row.key}</span>
                {row.value === null ? (
                  <span className="rows__absent" title={row.because}>
                    {row.because ?? "Not published"}
                  </span>
                ) : (
                  <span className="rows__value">{row.value}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {props.thumbs === undefined ? null : (
          <div className="thumbs">
            {props.thumbs.map((thumb) => (
              <span key={thumb.label} className="thumbs__item">
                <span className="glyph" aria-hidden="true">
                  {thumb.glyph}
                </span>
                <span className="thumbs__label">{thumb.label}</span>
              </span>
            ))}
          </div>
        )}

        {props.note === undefined ? null : (
          <div className={`note note--${props.note.kind}`}>
            <span className="glyph" aria-hidden="true">
              {props.note.kind === "pending" ? "sync" : "info"}
            </span>
            <span>{props.note.text}</span>
          </div>
        )}

        <div className="actions">
          {(props.actions ?? []).map((action) => (
            <Action key={action.label} {...action} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The state chip: a glyph and a word, never a colour alone.
 *
 * `unknown` is the only class that renders text inside its glyph — a literal `?`, as the design's own
 * `chipQ` does — and it is the class this application reaches for whenever the platform could not
 * confirm something.
 */
export function StateChip({
  chipClass,
  label,
}: {
  readonly chipClass: ChipClass;
  readonly label: string;
}): ReactNode {
  return (
    <span className={`chip chip--${chipClass}`}>
      <span className="chip__glyph" aria-hidden="true">
        {chipClass === "unknown" ? "?" : ""}
      </span>
      {label}
    </span>
  );
}

/**
 * One 56dp action, and the slide variant for anything that cannot be undone.
 *
 * The design's note: _"slide-to-confirm for accept, arrive, start and complete so a pocket tap
 * can't trigger them."_ The knob is a 48dp white square at the left of the track and the label is
 * centred in the remaining width, which is what the design's `paddingLeft:48` achieves.
 *
 * `aria-disabled` rather than `disabled` on an action with no published route: a control a screen
 * reader cannot reach is worse than one that explains itself, and the explanation is the whole
 * content of the press.
 */
function Action(action: ActionProps): ReactNode {
  const classes = [
    "action",
    action.primary ? "action--primary" : undefined,
    action.slide === true ? "action--slide" : undefined,
  ]
    .filter((name) => name !== undefined)
    .join(" ");

  return (
    <button
      type="button"
      className={classes}
      {...(action.unavailable === undefined
        ? {}
        : { "aria-disabled": true, title: action.unavailable })}
      onClick={action.unavailable === undefined ? action.onPress : undefined}
    >
      <span className="glyph action__glyph" aria-hidden="true">
        {action.slide === true ? "double_arrow" : action.glyph}
      </span>
      {action.label}
    </button>
  );
}

/*
 * The schematic map is **not** drawn, and that is a reading of the design rather than an omission.
 *
 * `11 Driver App.dc.html` marks four screens `map:1` and builds five absolutely positioned layers
 * for them — a 26px grid, a rotated road, an elbow of route, a `package_2` pin and a
 * `two_wheeler` marker. None of it reaches the page. The file's own `phones` object sets
 * `layers` twice: once mapped into `{s, t}` pairs the template reads, and again a few properties
 * later as the bare shorthand `layers,`. The second wins, so every `ly.s` the template asks for is
 * `undefined` and the five spans render unstyled and invisible.
 *
 * Both approved artefacts agree with that, which is what settles it. The design file rendered in a
 * browser draws an empty hero on `7 Document expiring`, and so does
 * `reference-screenshots/Mobile/Driver App/09 Document expiring.jpg` at all three widths — the
 * screenshot the design team cut and approved. `CLAUDE.md`'s acceptance check requires a comparison
 * against both, and both show no map.
 *
 * So this build draws what was approved: the map hero's height and its flat `C.map` background, and
 * nothing in it. Drawing a map that neither the design nor its reference screenshot contains would
 * be adding something that is not in the design, which is the rule next to the one about not removing
 * things. Recorded as Q-68, with the duplicate key named, so the design team can decide which of the
 * two they meant — and if the map was meant, the geometry is in their file and in this note.
 */
